/**
 * Pipeline symbol shared types.
 * Keep category / family unions here so catalog + drawers stay in sync.
 */

export type PipelineSymbolCategory =
  | 'flow'
  | 'gateway'
  | 'event'
  | 'io'
  | 'queue'
  | 'data'
  | 'notify'
  | 'integration'
  | 'software'
  | 'cicd'
  | 'security'
  | 'people'
  | 'governance'
  | 'project'
  | 'manufacturing'
  | 'logistics'
  | 'quality'
  | 'industrial'
  | 'cloud'
  | 'ai'
  | 'status'
  | 'layout'
  | 'facilities'
  | 'transport'
  | 'nature'
  | 'devices';

export interface PipelineSymbolMeta {
  kind: string;
  label: string;
  category: PipelineSymbolCategory;
}

export type PipelineCatalogRow = [string, string, PipelineSymbolCategory];

/** Local glyph canvas size (px). */
export const PIPELINE_GLYPH_SIZE = 48;

export type PipelineGlyphFamily =
  | 'capsuleStart'
  | 'capsuleEnd'
  | 'process'
  | 'subprocess'
  | 'userTask'
  | 'serviceTask'
  | 'manualTask'
  | 'scriptTask'
  | 'businessRule'
  | 'diamond'
  | 'gatewayPlus'
  | 'gatewayX'
  | 'gatewayCircle'
  | 'gatewayMerge'
  | 'circleEvent'
  | 'signal'
  | 'escalation'
  | 'timer'
  | 'message'
  | 'error'
  | 'warning'
  | 'info'
  | 'comment'
  | 'chevronIn'
  | 'chevronOut'
  | 'arrow'
  | 'pipelineFlow'
  | 'valueStream'
  | 'stack'
  | 'document'
  | 'folder'
  | 'cylinder'
  | 'chart'
  | 'phone'
  | 'api'
  | 'globe'
  | 'git'
  | 'build'
  | 'compile'
  | 'deploy'
  | 'rollback'
  | 'cicd'
  | 'env'
  | 'shield'
  | 'person'
  | 'people'
  | 'check'
  | 'flag'
  | 'coin'
  | 'change'
  | 'machine'
  | 'robot'
  | 'conveyor'
  | 'warehouse'
  | 'inventory'
  | 'crate'
  | 'wrench'
  | 'truck'
  | 'forklift'
  | 'hexService'
  | 'system'
  | 'sensor'
  | 'cloud'
  | 'server'
  | 'ai'
  | 'tag'
  | 'lane'
  | 'pin'
  | 'loop'
  | 'retry'
  | 'exception'
  | 'trigger'
  | 'cnc'
  | 'productionLine'
  | 'assembly'
  | 'crane'
  | 'pallet'
  | 'cargoContainer'
  | 'packaging'
  | 'rawMaterial'
  | 'wip'
  | 'finishedGoods'
  | 'scrap'
  | 'appContainer'
  | 'camera'
  | 'barcode'
  | 'rfid'
  | 'iot'
  | 'calendar'
  | 'chat'
  | 'k8s'
  | 'vm'
  | 'workstation'
  | 'loadingDock'
  | 'waste'
  | 'manufacturingCell'
  | 'backup'
  | 'archiveStorage'
  | 'customerDelivery'
  | 'agv'
  | 'activity'
  | 'operation'
  | 'taskBox'
  | 'workflow'
  | 'businessProcess'
  | 'manufacturingProcess'
  | 'engineeringProcess'
  | 'softwareProcess'
  | 'wait'
  | 'linkChain'
  | 'dependency'
  | 'annotation'
  | 'buffer'
  | 'fork'
  | 'join'
  | 'split'
  | 'module'
  | 'file'
  | 'dataObject'
  | 'report'
  | 'log'
  | 'webhook'
  | 'email'
  | 'notification'
  | 'microservice'
  | 'branch'
  | 'mergeRequest'
  | 'prototype'
  | 'simulation'
  | 'kpi'
  | 'metric'
  | 'analytics'
  | 'secretVault'
  | 'firewall'
  | 'encryption'
  | 'authorization'
  | 'review'
  | 'inspection'
  | 'approval'
  | 'validation'
  | 'handover'
  | 'milestone'
  | 'phase'
  | 'sprint'
  | 'goal'
  | 'objective'
  | 'cadModel'
  | 'schematicDoc'
  | 'blockDiagram'
  | 'bom'
  | 'scanner'
  | 'eventBus'
  | 'broker'
  | 'messageQueue'
  | 'dataset'
  | 'library'
  | 'package'
  | 'artifact'
  | 'release'
  | 'monitoring'
  | 'timeline'
  | 'issue'
  | 'risk'
  | 'unitTest'
  | 'integrationTest'
  | 'functionalTest'
  | 'acceptanceTest'
  | 'reliabilityTest'
  | 'qualityControl'
  | 'qualityAssurance'
  | 'testBench'
  | 'calibration'
  | 'repair'
  | 'customer'
  | 'engineer'
  | 'manager'
  | 'operator'
  | 'technician'
  | 'vendor'
  | 'pool'
  | 'decisionPoint'
  | 'checkpoint'
  | 'synchronization'
  | 'source'
  | 'destination'
  | 'flowPath'
  | 'verification'
  | 'signOff'
  | 'certification'
  | 'version'
  | 'factory'
  | 'building'
  | 'home'
  | 'laptop'
  | 'gps'
  | 'earth'
  | 'starMark'
  | 'sun'
  | 'window'
  | 'solarPanel'
  | 'plant'
  | 'forest'
  | 'hillStation'
  | 'map'
  | 'umbrella'
  | 'car'
  | 'van'
  | 'bike'
  | 'cycle'
  | 'rickshaw'
  | 'autoVehicle'
  | 'bus'
  | 'flight'
  | 'helicopter'
  | 'ship'
  | 'boat'
  | 'road'
  | 'staircase'
  | 'lift'
  | 'park'
  | 'parking'
  | 'school'
  | 'stadium'
  | 'shop'
  | 'market'
  | 'dollar'
  | 'euro'
  | 'rupee'
  | 'envelope'
  | 'virus'
  | 'bug'
  | 'insect'
  | 'bluetooth'
  | 'phoneTower'
  | 'wifi'
  | 'light'
  | 'temple'
  | 'compliance';
